import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { Camera, Trash, Image as ImageIcon } from '@phosphor-icons/react'
import { ImageCropDialog } from '@/components/account/ImageCropDialog'
import type { Table } from '@/types'

const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB para imagem original
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface EditTableDialogProps {
  table?: Table
  tableData?: Table
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdateTable: (updates: Partial<Table>, imageFile?: File) => void
  isLoading?: boolean
}

export function EditTableDialog({ table, tableData, open, onOpenChange, onUpdateTable, isLoading }: EditTableDialogProps) {
  // Use table ou tableData dependendo de qual foi passado
  const currentTable = table || tableData
  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [subject, setSubject] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [isActive, setIsActive] = useState(true)
  
  // Estados para imagem
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [showCropDialog, setShowCropDialog] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Preencher campos com dados da mesa atual quando abrir o diálogo
  useEffect(() => {
    if (currentTable) {
      setName(currentTable.name)
      setDescription(currentTable.description)
      setSubject(currentTable.subject)
      const existingImage = currentTable.imageUrl || currentTable.image_url || ''
      setImageUrl(existingImage)
      setImagePreview(existingImage || null)
      setIsActive(currentTable.is_active)
      // Limpar arquivo de imagem ao abrir (será usado apenas se usuário selecionar nova)
      setImageFile(null)
    }
  }, [currentTable])

  // Funções de manipulação de imagem
  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WebP ou GIF.')
      return
    }

    // Validar tamanho
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Arquivo muito grande. Máximo permitido: 10MB')
      return
    }

    // Criar URL para o cropper
    const reader = new FileReader()
    reader.onload = (e) => {
      setCropImageSrc(e.target?.result as string)
      setShowCropDialog(true)
    }
    reader.readAsDataURL(file)
    
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = (croppedFile: File) => {
    // Criar preview da imagem recortada
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(croppedFile)
    
    setImageFile(croppedFile)
    setCropImageSrc(null)
    // Limpar URL antiga já que teremos um novo arquivo
    setImageUrl('')
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setImageUrl('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!currentTable || !name.trim() || !description.trim() || !subject) {
      toast.error('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    onUpdateTable({
      name: name.trim(),
      description: description.trim(),
      subject,
      image_url: imageFile ? undefined : (imageUrl.trim() || undefined),
      is_active: isActive
    }, imageFile || undefined)

    onOpenChange(false)
  }

  const subjects = [
    'Matemática',
    'Ciências',
    'História',
    'Literatura',
    'Geografia',
    'Física',
    'Química',
    'Biologia',
    'Ciência da Computação',
    'Língua Portuguesa',
    'Estudos Sociais',
    'Arte e Criatividade',
    'Filosofia',
    'Economia',
    'Psicologia'
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Campanha</DialogTitle>
          <DialogDescription>
            Atualize as informações da sua campanha educacional.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-campaign-name">Nome da Campanha</Label>
            <Input
              id="edit-campaign-name"
              placeholder="Digite o nome da campanha"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="edit-campaign-subject">Matéria</Label>
            <Select value={subject} onValueChange={setSubject} required>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a área de estudo" />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((subj) => (
                  <SelectItem key={subj} value={subj}>
                    {subj}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="edit-campaign-description">Descrição</Label>
            <Textarea
              id="edit-campaign-description"
              placeholder="Descreva o cenário da campanha e os objetivos de aprendizado"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </div>
          
          {/* Upload de Imagem */}
          <div className="space-y-2">
            <Label>Imagem da Campanha (Opcional)</Label>
            <div className="flex items-center gap-3">
              {/* Preview da imagem */}
              <div 
                className="relative w-32 h-20 rounded-lg overflow-hidden bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={handleImageClick}
              >
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={24} className="text-muted-foreground" />
                )}
              </div>
              
              {/* Botões de ação */}
              <div className="flex flex-col gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleImageClick}
                >
                  <Camera size={16} className="mr-2" />
                  {imagePreview ? 'Trocar' : 'Carregar'}
                </Button>
                
                {imagePreview && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={handleRemoveImage}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash size={16} className="mr-2" />
                    Remover
                  </Button>
                )}
              </div>
            </div>
            
            {/* Input file oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="edit-campaign-status"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="edit-campaign-status">
              Campanha {isActive ? 'Ativa' : 'Pausada'}
            </Label>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={isLoading}>
              Salvar Alterações
            </Button>
          </div>
        </form>
      </DialogContent>
      
      {/* Modal de Crop */}
      {cropImageSrc && (
        <ImageCropDialog
          open={showCropDialog}
          onOpenChange={setShowCropDialog}
          imageSrc={cropImageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={16/9}
          outputSize={800}
          cropShape="rect"
          title="Ajustar Imagem da Campanha"
          description="Arraste para posicionar e use o zoom para enquadrar a imagem."
        />
      )}
    </Dialog>
  )
}